import type { CategoryEnum } from 'src/api/main'
import { readLocalized, toBaseLang, type LocalizedField } from 'src/utils'

import { isOwnerRole, PERMISSION_CATEGORY_ORDER } from './constants'
import type { EditableWorkspaceRole, WorkspacePermissionCatalog } from './types'

export { readLocalized, toBaseLang }

export type PermissionGroup = {
  key: CategoryEnum
  label: string
  permissions: WorkspacePermissionCatalog[]
}

/**
 * The catalog serves `label`/`description` as free-form localized JSON, which
 * the generator widens to `Record<string, unknown>`. Narrow it back to the
 * shape the shared reader expects.
 */
export const readPermissionText = (
  value: WorkspacePermissionCatalog['label'],
  lang: string
): string => readLocalized(value as LocalizedField, lang)

/** Client-side search over a catalog row's code, label and description. */
export const permissionMatchesQuery = (
  permission: WorkspacePermissionCatalog,
  query: string,
  lang: string
): boolean => {
  if (!query) return true

  return [
    permission.code,
    readPermissionText(permission.label, lang),
    readPermissionText(permission.description, lang)
  ].some((text) => text.toLowerCase().includes(query))
}

/**
 * Buckets the catalog by the backend's `category` enum. Groups follow
 * `PERMISSION_CATEGORY_ORDER`; a category the backend adds later sorts to the
 * end by label. `labelFor` resolves a category to its translated name.
 */
export const groupPermissions = (
  permissions: WorkspacePermissionCatalog[],
  labelFor: (category: CategoryEnum) => string
): PermissionGroup[] => {
  const byCategory = new Map<CategoryEnum, WorkspacePermissionCatalog[]>()
  for (const p of permissions) {
    const bucket = byCategory.get(p.category)
    if (bucket) bucket.push(p)
    else byCategory.set(p.category, [p])
  }

  return Array.from(byCategory.entries())
    .map(([key, items]) => ({
      key,
      label: labelFor(key),
      permissions: items.slice().sort((a, b) => a.code.localeCompare(b.code))
    }))
    .sort((a, b) => {
      const ai = PERMISSION_CATEGORY_ORDER.indexOf(a.key)
      const bi = PERMISSION_CATEGORY_ORDER.indexOf(b.key)
      if (ai !== -1 && bi !== -1) return ai - bi
      if (ai !== -1) return -1
      if (bi !== -1) return 1

      return a.label.localeCompare(b.label)
    })
}

/**
 * `{ [category]: grantedPermissionIds }` for one role — the shape the matrix
 * form holds a role column in.
 */
export const getRoleInitialPermissions = (
  rolePermissions: ReadonlyArray<{ id: number }> | undefined,
  groups: PermissionGroup[]
): Record<string, number[]> => {
  const granted = new Set((rolePermissions ?? []).map((p) => p.id))

  return Object.fromEntries(
    groups.map((group) => [
      group.key,
      group.permissions.filter((p) => granted.has(p.id)).map((p) => p.id)
    ])
  )
}

/**
 * Owner and admin roles hold every workspace permission implicitly — the
 * backend grants them without listing the codes, so their cells render as
 * fully selected regardless of what `permissions` contains.
 */
export const roleGrantsEveryPermission = (
  role: EditableWorkspaceRole
): boolean => isOwnerRole(role.codename) || role.is_admin

/**
 * Roles no client may write to. Owner and admin are implicitly full, and a
 * system role is read-only outright — the backend answers a PATCH of either
 * with `system_workspace_role_read_only`.
 */
export const isWorkspaceRoleReadOnly = (role: EditableWorkspaceRole): boolean =>
  roleGrantsEveryPermission(role) || role.is_system === true
